import React, { Children, createContext, isValidElement, useContext } from "react";
import Reveal, { type RevealProps } from "./Reveal";

interface StaggerContextValue {
    stagger: number;
}

const StaggerContext = createContext<StaggerContextValue | null>(null);

export function useStaggerDelay(index = 0): number {
    const context = useContext(StaggerContext);
    if (!context) {
        return 0;
    }
    return index * context.stagger;
}

interface StaggerGroupProps {
    stagger?: number;
    children: React.ReactNode;
}

/**
 * Provides stagger delay context for child Reveal components.
 * Automatically assigns staggerIndex to direct Reveal children.
 */
export const StaggerGroup: React.FC<StaggerGroupProps> = ({
    stagger = 0.08,
    children,
}) => {
    let revealIndex = 0;

    const staggeredChildren = Children.map(children, (child) => {
        if (!isValidElement(child)) {
            return child;
        }

        if (child.type === Reveal) {
            const currentIndex = revealIndex;
            revealIndex += 1;
            const revealChild = child as React.ReactElement<RevealProps>;
            return React.cloneElement(revealChild, {
                staggerIndex: revealChild.props.staggerIndex ?? currentIndex,
            });
        }

        return child;
    });

    return (
        <StaggerContext.Provider value={{ stagger }}>
            {staggeredChildren}
        </StaggerContext.Provider>
    );
};

export default StaggerGroup;
